import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams, useLocation } from "react-router-dom";
import { TEMPLATE_QUERY_PARAM, DEFAULT_TEMPLATE_PARAM_VALUE } from "../constants/routes";

const EMPTY_TEMPLATES = [];

export function useActivityTemplates(initialTemplateId) {
  const userTemplates = useSelector(
    (state) => state.sessionContextOidc?.userView?.templates ?? EMPTY_TEMPLATES,
  );

  const templates = useMemo(
    () =>
      userTemplates
        .filter((t) => t.module === "activity")
        .sort((a, b) => {
          return a.name.localeCompare(b.name);
        })
        .map((t) => ({
          id: t.template_id ?? t.id,
          name: t.name,
          template_short_name: t.short_name,
        })),
    [userTemplates],
  );

  const resolveShortName = useCallback(
    (templateId) => {
      if (!templateId) return null;
      const match = templates.find((t) => String(t.id) === String(templateId));
      return match?.template_short_name ?? null;
    },
    [templates],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const locationStateRef = useRef(location.state);
  locationStateRef.current = location.state;
  const templateParam = searchParams.get(TEMPLATE_QUERY_PARAM);

  const [selectedTemplateShortName, setSelectedTemplateShortNameState] = useState(() => {
    if (templateParam && templateParam !== DEFAULT_TEMPLATE_PARAM_VALUE) return templateParam;
    return resolveShortName(initialTemplateId);
  });

  useEffect(() => {
    if (initialTemplateId && !templateParam) {
      const short = resolveShortName(initialTemplateId);
      if (short) setSelectedTemplateShortName(short);
    }
  }, [initialTemplateId, templateParam, resolveShortName]);

  const setSelectedTemplateShortName = useCallback((shortName) => {
    setSelectedTemplateShortNameState(shortName || null);
    setSearchParams(
      (previousParams) => {
        const updatedParams = new URLSearchParams(previousParams);
        updatedParams.set(TEMPLATE_QUERY_PARAM, shortName || DEFAULT_TEMPLATE_PARAM_VALUE);
        return updatedParams;
      },
      { replace: true, state: locationStateRef.current },
    );
  }, [setSearchParams]);

  const selectedTemplateName = useMemo(() => {
    if (!selectedTemplateShortName) return null;
    const m = templates.find(
      (t) => t.template_short_name === selectedTemplateShortName,
    );
    return m?.name ?? null;
  }, [templates, selectedTemplateShortName]);

  return {
    templates,
    areTemplatesLoading: false,
    templatesError: null,
    selectedTemplateShortName,
    setSelectedTemplateShortName,
    selectedTemplateName,
  };
}
